import * as React from 'react';
import { AnsViewComp } from '@wix/answers-app-core';

import { submitTicket, uploadBase64File } from '../../api';
import { BaseWidgetProps } from '../../widget-container';

import * as ans from '@wix/answers-api';
import { ContactForm } from '@wix/answers-common-components';
import { EventType } from '../../../common/logger/event-type';
import { contentToHtml, closeWidget } from '../../../common/utils';
import { fedopsInteractions } from '../../../common/fedops-interactions';
import { noop, getIdealTextColor, TranslateFn } from '@wix/answers-lib';
import { Icon, close, DeprecatedText, DeprecatedButton } from '@wix/answers-ui-libby';
import { NavigationState } from '../../../common/navigator';

export type ContactPageProps = {
	baseConfig: BaseWidgetProps
	mode?: ContactPageMode
};

export enum ContactPageMode {
	CHAT_CLOSED_CONTACT_PAGE = 0,
	FORM_CONTACT_PAGE = 1
}

export const ContactPageKey = `contact-page`;

export type ContactPageState = {
	chatSettings: ans.WidgetChatSettings;
	submitted: boolean;
};

const emptyInitialFieldData: ans.ContactFormInitialFieldData = {
	fullname: '',
	email: '',
	title: '',
	description: '',
	customFields: {},
	attachments: []
};

export class ContactPage extends AnsViewComp<ContactPageProps, ContactPageState> {

	state: ContactPageState = {
		chatSettings: this.props.baseConfig.widgetConfig.chatModuleSettings,
		submitted: false
	};

	contactLogger: ans.ContactLogger = {
		submit: (data) => this.logEvent(EventType.SUBMIT_TICKET_ATTEMPT, data),
		validationError: (data, invalidFields) => {
			this.logEvent(EventType.SUBMIT_TICKET_VALIDATION_ERROR, { ...data, invalidFields });
		},
		submitSuccess: (data, ticketId) => this.logEvent(EventType.SUBMIT_TICKET_SUCCESS, { ...data, ticketId }),
		submitFailure: (data) => this.logEvent(EventType.SUBMIT_TICKET_FAIL, data),
		cancel: (data) => this.logEvent(EventType.SUBMIT_TICKET_CANCEL, data),
		suggestedArticleSelected: (data, article) => {
			this.logEvent(EventType.SUBMIT_TICKET_SELECT_SUGGESTED_ARTICLE, { ...data, article });
		}
	};

	logEvent = (type: EventType, data: any) => this.props.baseConfig.logger.logEvent(type, data);

	tp: TranslateFn = (key, params) => {
		return this.props.t(`${ContactPageKey}.${key}`, params);
	}

	componentDidMount () {
		if (window) {
			window.addEventListener('message', this.handleSettingsPostMessage);
		}
	}

	componentWillUnmount () {
		if (window) {
			window.removeEventListener('message', this.handleSettingsPostMessage);
		}
	}

	handleSettingsPostMessage = (event: MessageEvent) => {
		const newSettings: any = event.data.previewNewSettings;
		if (newSettings && newSettings.chatModule) {
			this.setState({chatSettings: newSettings.chatModule});
		}
	}

	submitTicket = (submitData: ans.ContactFormSubmitData): Promise<any> => {
		this.props.baseConfig.getFedopsLogger().interactionStarted(fedopsInteractions.submitTicket);
		const htmlContent = contentToHtml(submitData.description);

		const { attachments } = submitData;
		const content = htmlContent;

		const alteredSubmitData = {
			subject: submitData.title,
			customFields: submitData.customFieldsValuesMap,
			captcha: submitData.captchaToken,
			content,
			useInvisible: true,
			private: true,
			userFirstName: submitData.firstName,
			userLastName: submitData.lastName,
			userEmail: submitData.email,
			locale: this.props.baseConfig.locale,
			sourceType: ans.SourceType.WIDGET,
			sourceId: this.props.baseConfig.widgetId,
			labelIds: [],
			attachments
		};

		return submitTicket(this.props.baseConfig.apiUrl, alteredSubmitData)
			.then(() => {
				this.props.baseConfig.getFedopsLogger().interactionEnded(fedopsInteractions.submitTicket);
				this.setState({submitted: true});
			})
			.catch(() => {
				// TODO - handle error
				this.setState({submitted: true});
				this.props.baseConfig.getFedopsLogger().interactionEnded(fedopsInteractions.submitTicket);
			});
	}

	onAttachmentUpload = (file: any, captchaToken?: string, progressCb?: (precentage: number) => void) => {
		return uploadBase64File(this.props.baseConfig.apiUrl, file, captchaToken, progressCb)
			.then((data: any) => ({ name: data.filename, url: data.filelink }));
	}

	onGetSuggestedArticles = async () => {
		return [];
	}

	render () {
		const { props } = this;
		const { baseConfig } = props;

		const translate = baseConfig.translate;
		const moduleSettings = baseConfig.widgetConfig.ticketModuleSettings || {};
		const contactMethod = moduleSettings.contactMethod;
		const contactFormTitle = moduleSettings.title ? moduleSettings.title : translate('widget-ng.contact.main.title');
		const contactFormSubTitle =
			moduleSettings.subTitle ? moduleSettings.subTitle : translate('widget-ng.contact.sub.title');
		const responseTimeMessage = moduleSettings.responseTimeMessage ?
			moduleSettings.responseTimeMessage : translate('widget-ng.contact.response-time-message');

		const chatContactSettings = (this.state.chatSettings.outsideBusinessHoursSettings || {}).contactForm || {};
		const chatContactFormTitle = chatContactSettings && chatContactSettings.title ? chatContactSettings.title
			: translate('widget-ng.contact.main.chat-contact-form-title');

		const initialFieldData: ans.ContactFormInitialFieldData = emptyInitialFieldData;
		const customFields: ans.ResolvedCustomField[] =
			(props.mode === ContactPageMode.CHAT_CLOSED_CONTACT_PAGE ? chatContactSettings.formFields as any
				: contactMethod.resolvedFormFields)
			|| [];

		const reCaptchaData: ans.ContactFormCaptchaData = {
			isRequired: false,
			position: 'inline',
			key: baseConfig.widgetConfig.publicReCaptchaKey
		};

		const brandColor = baseConfig.widgetConfig.appearance.brandColor;
		const idealTextColor = getIdealTextColor(baseConfig.widgetConfig.appearance.brandColor);

		const renderCloseContactForm = () => {
			const { isKbHomepage, goToState } = baseConfig.widgetNavigator;
			const goToKb = () => goToState(NavigationState.kb, {});
			if (isKbHomepage()) {
				return (
					<div className={'close-contact-form'} onClick={goToKb}>
						<Icon icon={close} />
					</div>
				);
			}
			return null;
		};

		const header = props.mode === ContactPageMode.CHAT_CLOSED_CONTACT_PAGE
			? (
				<div className='contact-header-chat' style={{ backgroundColor: brandColor }}>
					{renderCloseContactForm()}
					<div className='contact-title-chat' style={{ color: idealTextColor }}>
						{chatContactFormTitle}
					</div>
				</div>
			)
			: (
				<div className='contact-header-form' style={{ backgroundColor: brandColor }}>
					{renderCloseContactForm()}
					<div className='contact-title' style={{ color: idealTextColor }}>
						{contactFormTitle}
					</div>
					<div className='contact-description' style={{ color: idealTextColor }}>
						{contactFormSubTitle}
					</div>
					<div className='contact-responseTimeMessage' style={{ color: idealTextColor }}>
						{responseTimeMessage}
					</div>
				</div>
			);

		const maybeResponseTimeMessage = chatContactSettings.responseTimeMessageEnabled ? (
			<DeprecatedText type='t1a' className='success-response-time'>
				{chatContactSettings.responseTimeMessage || translate('widget-ng.chat.defaults.form-success-response-time')}
			</DeprecatedText>
		) : null;

		const successState = (
			<div className='success-state'>
				<div className='success-header' style={{ backgroundColor: brandColor, color: idealTextColor }}>
					{renderCloseContactForm()}
				</div>
				<div className='success-text-wrapper'>
					<DeprecatedText type='t0a' className='success-title'>
						{chatContactSettings.submitTitle || translate('widget-ng.chat.defaults.form-success-title')}
					</DeprecatedText>
					<DeprecatedText type='t2' className='success-subtitle'>
						{chatContactSettings.submitSubtitle || translate('widget-ng.chat.defaults.form-success-subtitle')}
					</DeprecatedText>
					{maybeResponseTimeMessage}
				</div>
				<DeprecatedButton size='extra-large' onClick={closeWidget}>
					{translate('widget-ng.chat.contact-form-success.got-it')}
				</DeprecatedButton>
			</div>
		);

		const mainContactForm = (
			<div className={'contact-page-wrapper'}>
				{header}
				<div className={'contact-form-top'} />
				<ContactForm
					translate={translate}
					initialFieldData={initialFieldData}
					customFields={customFields}
					onGetSuggestedArticles={this.onGetSuggestedArticles}
					reCaptchaData={reCaptchaData}
					onAttachmentUpload={this.onAttachmentUpload}
					onSubmit={this.submitTicket}
					onSuggestedArticleClick={noop}
					logger={this.contactLogger}
					locale={baseConfig.locale}
					primaryLocale={baseConfig.widgetConfig.tenant.primaryLocale}
				/>
			</div>
		);

		return (
			<div className='contact-page'>
				{this.state.submitted ? successState : mainContactForm}
			</div>
		);
	}
}
